import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, RendererStyleFlags2 } from "@angular/core";
import { Select, Store } from "@ngxs/store";
import { combineLatest, merge, Observable, Subject } from "rxjs";
import { ComponentModel } from "./report-component.state-models";
import { DeleteReportComponent, EditComponentConfigOptions, EditComponentOptions, InitComponentState, UpdateReportComponent } from "./report-component.state-actions";
import { ComponentState } from "./report-component.state";
import { GridsterConfig, GridsterItem } from "angular-gridster2";
import { FormControl, FormGroup } from "@angular/forms";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { BaseReportComponent, MeasureChartComponentData, ImageComponentData, ReportComponentType, ReportDataContext, TextComponentData, COMPONENT_CONFIG_DEFAULT, COMPONENT_LAYER_SWITCH_MOVED_NOTICE, COMPONENT_OVERLAP_WARNING, ComponentConfigOption, ComponentGeometry, IndexComponentData, GanttComponentData, GANTT_OPTION_DEFAULT, ResolvedTypography, DEFAULT_TYPOGRAPHY, TypographySettings, geometryCollides, findFreePosition as sharedFindFreePosition, resolveImageFocalPoint } from "@core/data/types/advanced-report";
import { ReportComponentTypographyDialog, ReportComponentTypographyDialogData } from "../report-dialogs/report-component-typography-dialog/report-component-typography.dialog";
import { resolveTypography } from "@core/utils";
import { FontLoaderService } from "@core/fonts/font-loader.service";
import { PageState } from "../report-page/report-page-state/report-page.state";
import { PageModel } from "../report-page/report-page-state/report-page.state-models";
import { EditModeHintService } from "../../services/edit-mode-hint.service";
import { AdvancedReportState, editModeCheck } from "../report-view/report-state/advanced-report.state";
import { AdvancedReportStateModel } from "../report-view/report-state/advanced-report.state-models";
import { Dialog, Snackbar } from "@core/material";
import { TableComponentData } from "@core/data/types/Reports/report-tables";
import { AddReportComponent, SourceComponentOptions } from '../report-page/report-page-state/report-page.state-actions';

@Component({
    selector: 'pv-report-component',
    templateUrl: 'report-component.view.html',
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
    host: {
        class: 'pv-report-component'
    }
})
export class ReportComponentView implements OnInit, OnDestroy {

    @Select(AdvancedReportState)
    reportState$: Observable<AdvancedReportStateModel>;

    @Select(PageState.getEntities)
    pageEntities$: Observable<{ [key: string]: PageModel }>;

    //? MAIN STATE
    componentState$: Observable<ComponentModel>;

    @Input()
    key: string;

    @Input()
    reportKey: string;

    @Input()
    shareKey: string = null;

    @Input()
    editMode: boolean = false;

    @Input()
    gridsterConfig: GridsterConfig;

    item: GridsterItem;

    optionsKey: string;

    componentHover: boolean = false;

    //? Resolved Report → Page → Component typography, fanned out to chart renderers.
    typography: ResolvedTypography = DEFAULT_TYPOGRAPHY;

    // The component's own font override, if set — surfaced as an edit-mode badge so an override is
    // visible without opening the ⋮ menu.
    get fontOverride(): string {
        return this._componentConfig?.inherited?.typography?.fontFamily ?? null;
    }

    //? Latest tier inputs, kept for the component typography override dialog.
    private _reportTypo: TypographySettings;
    private _pageTypo: TypographySettings;
    private _componentConfig: ComponentConfigOption;

    // Once the author starts actively dragging/zooming an image's crop, focalX/focalY/zoom are no
    // longer re-seeded from server echoes (the optimistic save patch, or the compile() reload
    // EditComponentOptions triggers afterward). Without this, a slightly-stale response arriving after
    // a newer local edit would silently overwrite it mid-interaction — this is what read as "jumping
    // back to its original position." Saves still happen normally via the debounced path; only the
    // locally-displayed value stops being resynced from async echoes once the author has touched it.
    private _focalTouched = false;
    // True from the moment a local layerIndex/radius/fillArea edit is dispatched until that specific
    // save round trip resolves — see updateConfig()/setupComponentControl().
    private _configDirty = false;
    // True from the moment a duplicate is dispatched until that create() round trip resolves — a
    // second click before then would still see this component's stale (pre-duplicate) geometry via
    // selectSnapshot, compute the identical target position, and land a second duplicate on top of
    // the first. Guards against exactly that fast-double-click race — see duplicateComponent().
    private _duplicating = false;

    // TODO CHECK IF IT IS NECESSARY FOR EACH TYPE TO HAVE IT'S OWN CONTROL
    //? COMPONENT DATA CONTROLS
    textControl: FormControl  = new FormControl(null);
    imageControl: FormControl = new FormControl(null);
    chartControl: FormControl = new FormControl(null);
    tableControl: FormControl = new FormControl(null);
    indexControl: FormControl = new FormControl(null);
    ganttControl: FormControl = new FormControl(GANTT_OPTION_DEFAULT);


    //? CONFIG
    layerIndexControl: FormControl = new FormControl(null);
    radiusControl: FormControl = new FormControl(null);
    fillAreaControl: FormControl = new FormControl(null);
    focalXControl: FormControl = new FormControl(null);
    focalYControl: FormControl = new FormControl(null);
    zoomControl: FormControl = new FormControl(null);


    configFormGroup: FormGroup = new FormGroup({
        layerIndex: this.layerIndexControl,
        radius: this.radiusControl,
        fillArea: this.fillAreaControl,
        focalX: this.focalXControl,
        focalY: this.focalYControl,
        zoom: this.zoomControl,
    });

    private _destroy$ = new Subject();

    constructor(
        private _store: Store,
        private _dialog: Dialog,
        private _snackbar: Snackbar,
        private _el: ElementRef,
        private _renderer: Renderer2,
        private _fontLoader: FontLoaderService,
        private _editHint: EditModeHintService,
    ) {}

    // In preview mode a click does nothing — flash the edit toggle to hint it must be enabled.
    // Ignored while selecting text so the hint only fires on a real "I tried to edit" click.
    onPreviewInteract() {
        if (this.editMode) return;

        const selection = window.getSelection && window.getSelection().toString();
        if (selection) return;

        this._editHint.requestEnable();
    }

    ngOnInit() {
        this._store.dispatch(new InitComponentState(this.key, this.reportKey, this.shareKey));

        this.componentState$ = this._store.select(ComponentState.getComponent(this.key));

        combineLatest([this.reportState$, this.pageEntities$, this.componentState$])
            .pipe(takeUntil(this._destroy$))
            .subscribe(([reportState, pageEntities, componentState]) => {
                const reportTypo = reportState?.entities?.[this.reportKey]?.report?.options?.config?.inherited?.typography;
                const pageKey = componentState?.component?.pageKey;
                const pageTypo = pageEntities?.[pageKey]?.page?.options?.config?.inherited?.typography;
                const componentTypo = componentState?.component?.options?.config?.inherited?.typography;

                this._reportTypo = reportTypo;
                this._pageTypo = pageTypo;
                this._componentConfig = componentState?.component?.options?.config;

                this.applyTypography(resolveTypography([reportTypo, pageTypo, componentTypo]));
            });

        this.componentState$.subscribe((state: ComponentModel) => {
            // Set the grid item as soon as dimensions are known (before options/data finish loading)
            // so the gridster-item renders and shows its loading skeleton in place.
            if (!state.component) return;

            const { height, width, x, y } = state.component;

            this.item = {
                x: x,
                y: y,
                cols: width,
                rows: height,
                ['itemRef']: this.key,
                layerIndex: state.component.options?.config?.layerIndex || COMPONENT_CONFIG_DEFAULT.layerIndex,
            }

            if (state.component.options) {
                this.optionsKey = state.component.options.key;
                this.setupComponentControl(state.component);
            }

        });

        this.textControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe(value => {

                if (!editModeCheck(this.editMode)) return;
                if (!this.optionsKey) return;

                let data: TextComponentData = {
                    content: value,
                }

                this._store.dispatch(new EditComponentOptions(this.key, data, this.optionsKey))
            })

        this.imageControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe(value => {

                if (!editModeCheck(this.editMode)) return;
                if (!this.optionsKey) return;

                // Context comes from the tab that produced the option (AD-7). Every selectable option is stamped by
                // the picker's loader, so the fallback is unreachable today — any future producer (e.g. the 2.4
                // upload path) MUST stamp its own context rather than rely on it: SAMPLE_IMAGE is stripped by
                // templating, so a mislabelled asset would lose its image the first time a template is saved.
                if (!value.context) console.warn('[ReportComponent] image option missing context — defaulting to SAMPLE_IMAGE');

                let data: ImageComponentData = {
                    context: value.context || ReportDataContext.SAMPLE_IMAGE,
                    dataKey: value.key,
                    fileRef: value.fileRef
                }

                this._store.dispatch(new EditComponentOptions(this.key, data, this.optionsKey))
            })

        this.chartControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe((value: MeasureChartComponentData) => {

                if (!editModeCheck(this.editMode)) return;
                if (!this.optionsKey) return;

                let data: MeasureChartComponentData = {
                    ...value
                };

                this._store.dispatch(new EditComponentOptions(this.key, data, this.optionsKey))
            });

        this.tableControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe((value: TableComponentData) => {

                if (!editModeCheck(this.editMode)) return;
                if (!this.optionsKey) return;

                let data: TableComponentData = {
                    ...value
                };

                this._store.dispatch(new EditComponentOptions(this.key, data, this.optionsKey))
            });

        this.indexControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe((value: IndexComponentData) => {

                if (!editModeCheck(this.editMode)) return;
                if (!this.optionsKey) return;

                let data: IndexComponentData = {
                    ...value,
                };

                this._store.dispatch(new EditComponentOptions(this.key, data, this.optionsKey));
            });

        this.ganttControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe((value: GanttComponentData) => {
                if (!editModeCheck(this.editMode)) return;
                if (!this.optionsKey) return;

                this._store.dispatch(new EditComponentOptions(this.key, value, this.optionsKey));
            });

        this.layerIndexControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe((value: number) => {
                if (!editModeCheck(this.editMode)) return;

                const previousIndex = this.item.layerIndex;
                let index = this.setLayerIndex(value);

                if (!this.resolveLayerSwitchPlacement(index)) {
                    // Nowhere free on the target layer at all — revert rather than leave a
                    // silent overlap gridster itself will never detect or warn about (see
                    // resolveLayerSwitchPlacement's doc comment).
                    this.layerIndexControl.setValue(previousIndex, { emitEvent: false });
                    this._snackbar.warning(COMPONENT_OVERLAP_WARNING);
                    return;
                }

                this.layerIndexControl.setValue(index, { emitEvent: false });
                this.updateConfig()
            });

        this.radiusControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe((value: number) => {
                this.updateConfig();
            });

        this.fillAreaControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._destroy$)
            ).subscribe((value: boolean) => {
                this.updateConfig();
            });

        // Marks focal edits as locally-owned the instant they happen (not just once the debounced
        // save fires) so setupComponentControl never resyncs them from a racing/stale server echo —
        // see the _focalTouched field doc comment for why this matters.
        this.focalXControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this._focalTouched = true);
        this.focalYControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this._focalTouched = true);
        this.zoomControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this._focalTouched = true);

        // A SINGLE shared debounce across all three fields, not three independent ones. With three
        // separate debounced saves, a continuous drag-then-zoom gesture could fire multiple overlapping
        // PUT requests; if the network resolved them out of order, whichever response the server
        // processed last silently won — even overwriting a genuinely newer edit with a stale one. Merging
        // into one stream means at most one save per pause in interaction, regardless of which field(s)
        // changed.
        merge(
            this.focalXControl.valueChanges,
            this.focalYControl.valueChanges,
            this.zoomControl.valueChanges,
        ).pipe(
            debounceTime(500),
            takeUntil(this._destroy$)
        ).subscribe(() => {
            this.updateConfig();
        });
    }

    private applyTypography(typography: ResolvedTypography) {
        this.typography = typography;

        const host = this._el.nativeElement;

        // CSS var only — consumed by DOM renderers that should follow the report font (e.g. tables).
        // NOT applied to the text editor (it owns its font) or to the Culteva placeholder branding.
        this._renderer.setStyle(host, '--report-font-family', typography.fontFamily, RendererStyleFlags2.DashCase);

        // Load the family so a Chart.js canvas paints with the real font, not the fallback.
        this._fontLoader.ensure(typography.fontFamily);
    }

    updateConfig() {
        if (!editModeCheck(this.editMode)) return;

        // Owns layerIndex/radius/fillArea until this edit's own round trip resolves — otherwise a
        // componentState$ emission that lands mid-flight (e.g. the optimistic patch inside
        // EditComponentOptions itself) can setValue() these controls back to a stale server value,
        // visually "reverting" a toggle the user just made (setupComponentControl below).
        this._configDirty = true;

        // Spread the existing config so inherited typography survives a layer/radius change.
        const config = {
            ...COMPONENT_CONFIG_DEFAULT,
            ...this._componentConfig,
            ...this.configFormGroup.value
        }

        this._store.dispatch(new EditComponentConfigOptions(this.key, config))
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => { this._configDirty = false; },
                // a failed save must release the guard too — otherwise setupComponentControl()'s
                // `if (!this._configDirty)` check blocks every future server-state sync for this component
                error: () => { this._configDirty = false; },
            });
    }

    openTypography() {
        if (!editModeCheck(this.editMode)) return;

        const data: ReportComponentTypographyDialogData = {
            // What this component inherits today (report → page), shown as the baseline.
            parent: resolveTypography([this._reportTypo, this._pageTypo]),
            value: this._componentConfig?.inherited?.typography ?? {},
            // Name the actual source: the page value only wins if the page overrode it.
            parentLabel: this._pageTypo?.fontFamily ? 'From page' : 'From report',
        };

        this._dialog.open(ReportComponentTypographyDialog, { data, panelClass: 'reporting-theme' })
            .afterClosed()
            .subscribe(result => {
                if (!result?.status) return;

                const config: ComponentConfigOption = {
                    ...COMPONENT_CONFIG_DEFAULT,
                    ...this._componentConfig,
                    inherited: {
                        ...this._componentConfig?.inherited,
                        typography: result.data,
                    },
                };

                this._store.dispatch(new EditComponentConfigOptions(this.key, config));
            });
    }

    setupComponentControl(data: BaseReportComponent) {
        if (data.type === ReportComponentType.TEXT)  this.textControl.setValue(data.options?.data.content, { emitEvent: false });
        if (data.type === ReportComponentType.IMAGE) this.imageControl.setValue(data.options?.data, { emitEvent: false });
        if (data.type === ReportComponentType.CHART) this.chartControl.setValue(data.options?.data, { emitEvent: false });
        if (data.type === ReportComponentType.TABLE) this.tableControl.setValue(data.options?.data, { emitEvent: false });
        if (data.type === ReportComponentType.INDEX) this.indexControl.setValue(data.options?.data, { emitEvent: false });
        if (data.type === ReportComponentType.GANTT) this.ganttControl.setValue(data.options?.data ?? GANTT_OPTION_DEFAULT, { emitEvent: false });

        const focal = resolveImageFocalPoint(data.options?.config);

        // Once the author has locally touched focal/zoom, this component instance is the sole source
        // of truth for those three fields for the rest of the session — a server echo (optimistic
        // save patch, or the compile() reload EditComponentOptions triggers afterward) is never
        // allowed to overwrite a newer in-progress edit. See _focalTouched's doc comment.
        this.configFormGroup.setValue(
            {
                layerIndex: data.options?.config?.layerIndex ?? this.gridsterConfig.baseLayerIndex,
                radius: data.options?.config?.radius ?? COMPONENT_CONFIG_DEFAULT.radius,
                fillArea: data.options?.config?.fillArea ?? COMPONENT_CONFIG_DEFAULT.fillArea,
                focalX: this._focalTouched ? this.focalXControl.value : focal.focalX,
                focalY: this._focalTouched ? this.focalYControl.value : focal.focalY,
                zoom: this._focalTouched ? this.zoomControl.value : focal.zoom,
            },
            {
                emitEvent: false
            }
        );
        // an edit already in flight owns these fields until its own save resolves (see updateConfig()) —
        // otherwise a stale/intermediate emission here would visibly revert the toggle the user just made
        if (!this._configDirty) {
            this.configFormGroup.patchValue(
                {
                    layerIndex: data.options?.config?.layerIndex ?? this.gridsterConfig.baseLayerIndex,
                    radius: data.options?.config?.radius ?? COMPONENT_CONFIG_DEFAULT.radius,
                    fillArea: data.options?.config?.fillArea ?? COMPONENT_CONFIG_DEFAULT.fillArea,
                },
                {
                    emitEvent: false
                }
            );
        }
    }

    setLayerIndex(index: number) {
        const max = this.gridsterConfig.maxLayerIndex - 1;
        const min = this.gridsterConfig.baseLayerIndex;

        let newIndex = this.item.layerIndex + index;

        if (newIndex < min) newIndex = min;
        if (newIndex > max) newIndex = max;

        return newIndex;
    }

    // A layer switch reaches gridster through a plain [item] property update, which — unlike
    // component creation or a live drag/resize — never runs gridster's own collision check. Left
    // unchecked, switching onto an already-overlapping same-layer sibling would silently succeed,
    // leaving both components overlapping with no warning. Auto-nudges the component to the nearest
    // free position on the target layer when needed; returns false only when the target layer has
    // nowhere free at all, so the caller can revert instead.
    resolveLayerSwitchPlacement(newLayerIndex: number): boolean {
        const source: ComponentModel = this._store.selectSnapshot(ComponentState.getComponent(this.key));
        const component = source?.component;
        const pageKey = component?.pageKey;
        // Can't verify collision safety without the component/page — refuse rather than silently
        // permit the switch, which would recreate the exact unchecked-overlap bug this fix exists for.
        if (!component || !pageKey) return false;

        const width = this.item.cols;
        const height = this.item.rows;
        const geometry = { x: this.item.x, y: this.item.y, width, height };

        if (!this.collidesWithSibling(pageKey, newLayerIndex, geometry)) return true;

        const freeSpot = this.findFreePosition(pageKey, newLayerIndex, width, height, geometry.x, geometry.y);
        if (!freeSpot) return false;

        // Only confirm the move once it's actually saved — telling the user it moved when the save
        // failed would leave the server with the new layer (via updateConfig(), still called by the
        // caller regardless) but the old, colliding position: the exact overlap this fix prevents.
        this._store.dispatch(new UpdateReportComponent(this.key, {
            x: freeSpot.x,
            y: freeSpot.y,
            cols: width,
            rows: height,
        }))
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => this._snackbar.info(COMPONENT_LAYER_SWITCH_MOVED_NOTICE),
            });

        return true;
    }

    deleteComponent(event: boolean, key: string, pageKey: string) {
        if (!event) return;

        this._store.dispatch(new DeleteReportComponent(key, pageKey));
    }

    // Clones the component in place: same geometry, data and config, no creation form.
    // Placed just to the right of the original (or below it, if it wouldn't fit) so it never
    // lands exactly on top of its source.
    duplicateComponent(type: ReportComponentType, pageKey: string) {
        if (!editModeCheck(this.editMode)) return;
        if (this._duplicating) return;

        const source: ComponentModel = this._store.selectSnapshot(ComponentState.getComponent(this.key));
        const component = source?.component;
        if (!component) return;

        const maxCols = this.gridsterConfig.maxCols;
        const maxRows = this.gridsterConfig.maxRows;

        let x = this.item.x + this.item.cols;
        let y = this.item.y;

        if (x + this.item.cols > maxCols) {
            x = this.item.x;
            y = this.item.y + this.item.rows;
        }

        x = Math.max(0, Math.min(x, maxCols - this.item.cols));
        y = Math.max(0, Math.min(y, maxRows - this.item.rows));

        const layerIndex = component.options?.config?.layerIndex ?? this.gridsterConfig.baseLayerIndex;

        if (this.collidesWithSibling(pageKey, layerIndex, { x, y, width: this.item.cols, height: this.item.rows })) {
            this._snackbar.warning(COMPONENT_OVERLAP_WARNING);
            return;
        }

        const element: Partial<BaseReportComponent> = {
            pageKey,
            height: this.item.rows,
            width: this.item.cols,
            type: component.type,
            x,
            y,
            filter: component.filter ?? null,
        }

        const sourceOptions: SourceComponentOptions = {
            data: JSON.parse(JSON.stringify(component.options?.data ?? {})),
            config: JSON.parse(JSON.stringify(component.options?.config ?? COMPONENT_CONFIG_DEFAULT)),
        };

        this._duplicating = true;

        this._store.dispatch(new AddReportComponent(pageKey, element, sourceOptions))
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                complete: () => { this._duplicating = false; },
                error: () => { this._duplicating = false; },
            });
    }

    // All same-layer siblings' geometry, matching angular-gridster2's own checkCollisionTwoItems rule
    // (different layers never collide, per gridsterConfig's allowMultiLayer). Store-dependent, so it
    // can't move into the shared advanced-report.ts helpers below — each caller (this class, and
    // ReportView for brand-new components) fetches its own siblings and passes them to the shared
    // pure geometry functions.
    private getSameLayerSiblings(pageKey: string, layerIndex: number): ComponentGeometry[] {
        const page = this._store.selectSnapshot(PageState.getPage(pageKey));
        const componentKeys = page?.componentKeys ?? [];
        const entities = this._store.selectSnapshot(ComponentState.getEntities);

        return componentKeys.reduce((siblings, key) => {
            if (key === this.key) return siblings;

            const sibling = entities[key]?.component;
            if (!sibling) return siblings;
            // Guard against malformed geometry (NaN comparisons are always false, which would
            // silently treat a genuine sibling as non-colliding rather than skipping it explicitly).
            if (![sibling.x, sibling.y, sibling.width, sibling.height].every(Number.isFinite)) return siblings;

            const siblingLayer = sibling.options?.config?.layerIndex ?? this.gridsterConfig.baseLayerIndex;
            if (siblingLayer !== layerIndex) return siblings;

            siblings.push({ x: sibling.x, y: sibling.y, width: sibling.width, height: sibling.height });
            return siblings;
        }, [] as ComponentGeometry[]);
    }

    // Tests a single candidate position against same-layer siblings — used where the caller already
    // has one specific spot in mind (duplicateComponent()'s right-of/below candidate).
    private collidesWithSibling(pageKey: string, layerIndex: number, geometry: ComponentGeometry): boolean {
        const siblings = this.getSameLayerSiblings(pageKey, layerIndex);
        return siblings.some(sibling => geometryCollides(geometry, sibling));
    }

    // Searches for a free same-layer position — used by the layer-switch auto-nudge below, where,
    // unlike duplicateComponent(), there's no single obvious candidate to try first.
    private findFreePosition(pageKey: string, layerIndex: number, width: number, height: number, preferredX: number, preferredY: number): { x: number, y: number } | null {
        const siblings = this.getSameLayerSiblings(pageKey, layerIndex);
        return sharedFindFreePosition(siblings, width, height, this.gridsterConfig.maxCols, this.gridsterConfig.maxRows, preferredX, preferredY);
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

}